import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectCurrentToken, selectCurrentUser } from '../../features/auth/authSlice';
import { useGetUserDataQuery, useUpdateUserProfileMutation } from '../../features/api/apiSlice';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import { useToast } from '../../components/ui/Toast';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Icon from '../../components/AppIcon';
import PlacesAutocomplete from '../../components/ui/PlacesAutocomplete';
import AgentLayout from '../agent-dashboard/AgentLayout';
import UserLayout from '../user-dashboard/UserLayout';
import { isProfessionalRole } from '../../lib/authPortals';

const UserProfile = () => {
  const location = useLocation();
  const token = useSelector(selectCurrentToken);
  const authUser = useSelector(selectCurrentUser);
  const isUserDashboardShell = location.pathname.startsWith('/user-dashboard');
  const verificationHref = isProfessionalRole(authUser?.account_role)
    ? '/agent-dashboard/verification'
    : '/verification';

  const { data: userData, isLoading: isUserDataLoading, isError: isUserDataError, refetch: getUserData } =
    useGetUserDataQuery();
  const [updateUserProfile, { isLoading: isUpdatingProfile }] = useUpdateUserProfileMutation();
  const showToast = useToast();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    address: '',
    about_me: '',
    facebook_id: '',
    twiiter_id: '',
    instagram_id: '',
    pintrest_id: '',
    latitude: '',
    longitude: '',
    city: '',
    state: '',
    country: '',
  });

  useEffect(() => {
    if (token) {
      getUserData();
    }
  }, [token, getUserData]);

  useEffect(() => {
    if (userData && userData.data) {
      setFormData({
        name: userData.data.name || '',
        email: userData.data.email || '',
        mobile: userData.data.mobile || '',
        address: userData.data.address || '',
        about_me: userData.data.about_me || '',
        facebook_id: userData.data.facebook_id || '',
        twiiter_id: userData.data.twiiter_id || '',
        instagram_id: userData.data.instagram_id || '',
        pintrest_id: userData.data.pintrest_id || '',
        latitude: userData.data.latitude || '',
        longitude: userData.data.longitude || '',
        city: userData.data.city || '',
        state: userData.data.state || '',
        country: userData.data.country || '',
      });
    }
  }, [userData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await updateUserProfile(formData).unwrap();
      showToast('Profile updated successfully!', 'success');
    } catch (error) {
      showToast('Failed to update profile.', 'error');
      console.error('Failed to update profile:', error);
    }
  };

  const profileUser = userData?.data || authUser;
  const isVerified = profileUser?.verification_status === 'verified';

  const formBody = (
    <>
      {/* Verified badge notification */}
      <div
        className={`mb-6 rounded-xl border px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ${
          isVerified
            ? 'border-emerald-200 bg-emerald-50'
            : 'border-amber-200 bg-amber-50'
        }`}
      >
        <div className="flex items-start gap-3">
          <Icon
            name={isVerified ? 'BadgeCheck' : 'Shield'}
            size={22}
            className={isVerified ? 'text-emerald-600 mt-0.5' : 'text-amber-600 mt-0.5'}
          />
          <div>
            <p
              className={`font-semibold text-sm ${
                isVerified ? 'text-emerald-900' : 'text-amber-900'
              }`}
            >
              {isVerified ? 'Verified badge active' : 'No verified badge yet'}
            </p>
            <p className={`text-xs ${isVerified ? 'text-emerald-800/80' : 'text-amber-800/80'}`}>
              {isVerified
                ? 'Your trust badge is visible to users across HireBroker.'
                : isProfessionalRole(profileUser?.account_role)
                  ? 'Complete verification to earn your Real Estate Agent trust badge.'
                  : 'Verification is available for Real Estate Agents to build client trust.'}
            </p>
          </div>
        </div>
        {isProfessionalRole(profileUser?.account_role) && !isVerified && (
          <Link
            to={verificationHref}
            className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-amber-600 text-white text-sm font-medium hover:bg-amber-700"
          >
            Get verified
          </Link>
        )}
      </div>

      {isUserDataLoading ? (
        <p className="text-muted-foreground">Loading profile data…</p>
      ) : isUserDataError ? (
        <p className="text-error">Error loading profile data.</p>
      ) : (
        <div className="bg-card rounded-lg shadow-md border border-border p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-foreground">
                  Name
                </label>
                <Input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="mt-1 block w-full"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-foreground">
                  Email
                </label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="mt-1 block w-full"
                  disabled
                />
              </div>
              <div>
                <label htmlFor="mobile" className="block text-sm font-medium text-foreground">
                  Mobile
                </label>
                <Input
                  type="text"
                  id="mobile"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  className="mt-1 block w-full"
                />
              </div>
            </div>

            {/* Location — places search fills city/state/country/lat/lng */}
            <div
              className={`rounded-xl border p-4 sm:p-5 space-y-4 ${
                isProfessionalRole(profileUser?.account_role)
                  ? 'border-amber-200 bg-amber-50/50'
                  : 'border-border bg-muted/30'
              }`}
            >
              <div className="flex items-start gap-2">
                <Icon
                  name="MapPin"
                  size={18}
                  className={
                    isProfessionalRole(profileUser?.account_role)
                      ? 'text-amber-600 mt-0.5'
                      : 'text-slate-500 mt-0.5'
                  }
                />
                <div>
                  <p className="text-sm font-semibold text-foreground">
                    {isProfessionalRole(profileUser?.account_role)
                      ? 'Agent service location (required for map visibility)'
                      : 'Your location'}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {isProfessionalRole(profileUser?.account_role)
                      ? 'Search and select your office or main area so clients can find you on the agents map.'
                      : 'Search a place to auto-fill address fields and coordinates.'}
                  </p>
                </div>
              </div>
              <PlacesAutocomplete
                label="Search place"
                value={formData.address}
                placeholder="Start typing city or address…"
                onChange={(v) => setFormData((prev) => ({ ...prev, address: v }))}
                onPlaceSelected={(place) => {
                  setFormData((prev) => ({
                    ...prev,
                    address: place.address || place.formatted_address || prev.address,
                    city: place.city || prev.city,
                    state: place.state || prev.state,
                    country: place.country || prev.country,
                    latitude: place.latitude || prev.latitude,
                    longitude: place.longitude || prev.longitude,
                  }));
                }}
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="address" className="block text-sm font-medium text-foreground">
                  Address
                </label>
                <Input
                  type="text"
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className="mt-1 block w-full"
                />
              </div>
              <div>
                <label htmlFor="city" className="block text-sm font-medium text-foreground">
                  City
                </label>
                <Input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  className="mt-1 block w-full"
                />
              </div>
              <div>
                <label htmlFor="state" className="block text-sm font-medium text-foreground">
                  State
                </label>
                <Input
                  type="text"
                  id="state"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  className="mt-1 block w-full"
                />
              </div>
              <div>
                <label htmlFor="country" className="block text-sm font-medium text-foreground">
                  Country
                </label>
                <Input
                  type="text"
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  className="mt-1 block w-full"
                />
              </div>
              <div>
                <label htmlFor="latitude" className="block text-sm font-medium text-foreground">
                  Latitude
                </label>
                <Input
                  type="text"
                  id="latitude"
                  name="latitude"
                  value={formData.latitude}
                  onChange={handleChange}
                  className="mt-1 block w-full"
                  readOnly
                />
              </div>
              <div>
                <label htmlFor="longitude" className="block text-sm font-medium text-foreground">
                  Longitude
                </label>
                <Input
                  type="text"
                  id="longitude"
                  name="longitude"
                  value={formData.longitude}
                  onChange={handleChange}
                  className="mt-1 block w-full"
                  readOnly
                />
              </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="facebook_id" className="block text-sm font-medium text-foreground">
                  Facebook ID
                </label>
                <Input
                  type="text"
                  id="facebook_id"
                  name="facebook_id"
                  value={formData.facebook_id}
                  onChange={handleChange}
                  className="mt-1 block w-full"
                />
              </div>
              <div>
                <label htmlFor="twiiter_id" className="block text-sm font-medium text-foreground">
                  Twitter ID
                </label>
                <Input
                  type="text"
                  id="twiiter_id"
                  name="twiiter_id"
                  value={formData.twiiter_id}
                  onChange={handleChange}
                  className="mt-1 block w-full"
                />
              </div>
              <div>
                <label htmlFor="instagram_id" className="block text-sm font-medium text-foreground">
                  Instagram ID
                </label>
                <Input
                  type="text"
                  id="instagram_id"
                  name="instagram_id"
                  value={formData.instagram_id}
                  onChange={handleChange}
                  className="mt-1 block w-full"
                />
              </div>
              <div>
                <label htmlFor="pintrest_id" className="block text-sm font-medium text-foreground">
                  Pinterest ID
                </label>
                <Input
                  type="text"
                  id="pintrest_id"
                  name="pintrest_id"
                  value={formData.pintrest_id}
                  onChange={handleChange}
                  className="mt-1 block w-full"
                />
              </div>
            </div>
            <div>
              <label htmlFor="about_me" className="block text-sm font-medium text-foreground">
                About Me
              </label>
              <textarea
                id="about_me"
                name="about_me"
                value={formData.about_me}
                onChange={handleChange}
                rows="3"
                className="mt-1 block w-full border border-border rounded-md shadow-sm p-2 bg-background"
              />
            </div>
            <Button type="submit" disabled={isUpdatingProfile} className="w-full sm:w-auto">
              {isUpdatingProfile ? 'Updating…' : 'Update Profile'}
            </Button>
          </form>
        </div>
      )}
    </>
  );

  if (location.pathname.startsWith('/agent-dashboard')) {
    return (
      <AgentLayout title="My Profile" subtitle="Update your professional account details and trust badge status.">
        {formBody}
      </AgentLayout>
    );
  }

  // User dashboard shell
  if (isUserDashboardShell) {
    return (
      <UserLayout
        title="My Profile"
        subtitle="Update your account details and contact information."
      >
        <div className="max-w-4xl">{formBody}</div>
      </UserLayout>
    );
  }

  if (location.pathname === '/user-profile') {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="mb-6">
              <Breadcrumbs />
            </div>
            <h1 className="text-3xl font-bold text-foreground mb-6">My Profile</h1>
            {formBody}
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-6">
            <Breadcrumbs />
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-6">My Profile</h1>
          {formBody}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default UserProfile;
